<template>
    <div v-show="isLoaded" class="content-wrapper maintenance">
        <Modal
            :show="showModal"
            :title="$t('error.common.wrongFileExtension')"
            :enter-handler="enterHandler"
            style-class="modal-dialog modal-sm"
            @close="showModal = false"
        >
            <div>
                <p>
                    {{ $t('error.common.tryUploadWrongFileExtension') }}
                </p>
                <p>
                    {{ $t('common.allowedFiles', { val: '.fbak' }) }}
                </p>
            </div>
            <button class="btn btn-ok" @click="showModal = false">
                {{ $t('common.ok') }}
            </button>
        </Modal>
        <div class="system-information">
            <h4 class="section-title">{{ $t('maintenance.mariSystemInformation') }}</h4>
            <div class="state-item">
                <span>{{ $t('maintenance.version') }}</span>
                <span v-if="version">{{ version }}</span>
                <span v-else>{{ $t('common.na') }}</span>
            </div>
            <div class="state-item">
                <span>{{ $t('maintenance.currentTime') }}</span>
                <MariTimeClock />
            </div>
            <div class="state-item">
                <span>{{ $t('maintenance.timeZone') }}</span>
                <span v-if="timezone">{{ timezone }}</span>
                <span v-else>{{ $t('common.na') }}</span>
            </div>
        </div>
        <div class="state">
            <h4 class="section-title">{{ $t('maintenance.dbStatus') }}</h4>
            <div class="state-item">
                <span>{{ $t('maintenance.logStartDate') }}</span>
                <span v-if="startDate">{{ startDate }}</span>
                <span v-else>{{ $t('common.na') }}</span>
            </div>
            <div class="state-item">
                <span>{{ $t('maintenance.systemActivity') }}</span>
                <span>{{ activityHours }} ({{ $t('maintenance.logsHours') }})</span>
            </div>
            <div class="state-item">
                <span>{{ $t('maintenance.dbSize') }}</span> <span>{{ dbSize }} Mb</span>
            </div>
            <div class="state-item">
                <span>{{ $t('maintenance.logEntries') }}</span>
                <div class="progress">
                    <div
                        class="progress-bar"
                        role="progressbar"
                        :aria-valuenow="logPercent"
                        aria-valuemin="0"
                        aria-valuemax="100"
                        :style="{ width: logPercent + '%' }"
                    >
                        <span v-if="countLogs !== ''" class="progress-label">
                            {{ formattedCountLogs }} / 500 000
                        </span>
                        <span v-else>{{ $t('common.na') }}</span>
                    </div>
                </div>
            </div>
            <div class="state-buttons no-print">
                <button type="button" class="btn btn-add" @click="loadStatus(true)">
                    {{ $t('maintenance.buttons.refresh') }}
                </button>
                <button
                    type="button"
                    class="btn btn-undo"
                    :disabled="dbStatus.entries === 0"
                    @click="confirmClearLogs"
                >
                    {{ $t('maintenance.buttons.removeLogs') }}
                </button>
                <button type="button" class="btn btn-undo" @click="runLogParser">
                    {{ $t('maintenance.buttons.runLogParser') }}
                </button>
            </div>
        </div>
        <div class="export no-print">
            <h4>{{ $t('maintenance.export') }}</h4>
            <DateTimePickerForm v-model="exportDate" />
            <div class="export-bottom">
                <form method="post" target="_blank" action="/api/v2/maintenance/export/">
                    <input name="token" type="hidden" :value="authToken" />
                    <input name="from" type="hidden" :value="fromDateTime" />
                    <input name="to" type="hidden" :value="toDateTime" />
                    <button type="submit" class="btn btn-undo" :disabled="exportDate.invalid">
                        {{ $t('maintenance.buttons.export') }}
                    </button>
                </form>
            </div>
        </div>
        <div class="restore no-print">
            <div class="restore-item">
                <div class="restore-label">{{ $t('maintenance.backupFile') }}</div>
                <div class="backup-bottom">
                    <form method="post" target="_blank" action="/api/v2/maintenance/backup/">
                        <input name="token" type="hidden" :value="authToken" />
                        <button type="submit" class="btn btn-undo">
                            {{ $t('maintenance.buttons.download') }}
                        </button>
                    </form>
                </div>
            </div>
            <div class="restore-item">
                <DragArea :text="$t('common.dragging.dropFbakFile')" @onDropFiles="addDroppedFiles" />
                <span class="restore-label">{{ $t('maintenance.restoreSystemFromFile') }}</span>
                <div class="file-input-wrapper">
                    <div class="file-input-logic">
                        <label for="file-restore">
                            <i class="icon-file"></i>
                            <span v-if="file" class="selected-file" :title="file.name">{{ file.name }}</span>
                            <span v-else>{{ $t('maintenance.selectFile') }}</span>
                            <button v-if="file" @click.prevent="filesChange(null)">
                                <i class="icon-close"></i>
                            </button>
                        </label>
                        <input
                            id="file-restore"
                            ref="restore"
                            type="file"
                            class="custom-file-upload"
                            accept=".fbak"
                            @change="filesChange($event.target.files[0])"
                        />
                    </div>
                    <button :disabled="!file" type="button" class="btn btn-undo" @click="restore">
                        {{ $t('maintenance.buttons.restore') }}
                    </button>
                </div>
            </div>
        </div>
        <div class="audit-trail-logs">
            <div class="audit-trail-login">
                <h4 class="section-title">{{ $t('maintenance.auditTrailLoginTitle') }}</h4>
                <div class="state-item">
                    <span>{{ $t('maintenance.logStartDate') }}</span>
                    <span v-if="startDateAuditTrailLogin">{{ startDateAuditTrailLogin }}</span>
                    <span v-else>{{ $t('common.na') }}</span>
                </div>
                <div class="state-item">
                    <span>{{ $t('maintenance.dbSize') }}</span> <span>{{ dbAuditTrailLoginSize }} Mb</span>
                </div>
                <div class="state-item">
                    <span>{{ $t('maintenance.logEntries') }}</span>
                    <div class="progress">
                        <div
                            class="progress-bar"
                            role="progressbar"
                            :aria-valuenow="logAuditTrailLoginPercent"
                            aria-valuemin="0"
                            aria-valuemax="100"
                            :style="{ width: logAuditTrailLoginPercent + '%' }"
                        >
                            <span v-if="countLogs !== ''" class="progress-label">
                                {{ formattedCountAuditTrailLogin }} /
                                {{ formatCountLogs(MAX_AUDIT_TRAIL_EVENTS) }}
                            </span>
                            <span v-else>{{ $t('common.na') }}</span>
                        </div>
                    </div>
                </div>
                <button
                    type="button"
                    class="btn btn-undo no-print"
                    :disabled="dbAuditTrailStatus.entries === 0"
                    @click="confirmClearAuditTrailLogin"
                >
                    {{ $t('maintenance.buttons.removeLogs') }}
                </button>
            </div>
            <div class="audit-trail-room-history">
                <h4 class="section-title">{{ $t('maintenance.auditTrailRoomConfTitle') }}</h4>
                <div class="state-item">
                    <span>{{ $t('maintenance.logStartDate') }}</span>
                    <span v-if="startDateRoomHistory">{{ startDateRoomHistory }}</span>
                    <span v-else>{{ $t('common.na') }}</span>
                </div>
                <div class="state-item">
                    <span>{{ $t('maintenance.dbSize') }}</span> <span>{{ dbRoomHistorySize }} Mb</span>
                </div>
                <div class="state-item">
                    <span>{{ $t('maintenance.logEntries') }}</span>
                    <div class="progress">
                        <div
                            class="progress-bar"
                            role="progressbar"
                            :aria-valuenow="logRoomHistoryPercent"
                            aria-valuemin="0"
                            aria-valuemax="100"
                            :style="{ width: logRoomHistoryPercent + '%' }"
                        >
                            <span v-if="countLogs !== ''" class="progress-label">
                                {{ formattedCountRoomHistory }} /
                                {{ formatCountLogs(MAX_ROOM_HISTORY_EVENTS) }}
                            </span>
                            <span v-else>{{ $t('common.na') }}</span>
                        </div>
                    </div>
                </div>
                <button
                    type="button"
                    class="btn btn-undo no-print"
                    :disabled="dbRoomHistoryStatus.entries === 0"
                    @click="confirmClearRoomHistory"
                >
                    {{ $t('maintenance.buttons.removeLogs') }}
                </button>
            </div>
        </div>
    </div>
</template>

<script type="text/babel">
import { mapState } from 'vuex';
import format from 'date-fns/format';
import parseISO from 'date-fns/parseISO';

import DateTimePickerForm from './common/datetimepicker/DateTimePickerForm.vue';
import DragArea from './common/DragArea';
import MariTimeClock from './common/MariTimeClock.vue';
import mappingState from './common/mappingState';
import Modal from './common/Modal.vue';

import {
    resourceClearLogs,
    resourceMariInfo,
    resourceRestore,
    resourceRunLogParser,
    resourceStatus,
    resourceClearAuditTrailLogin,
    resourceClearRoomConfHistory,
    resourceRoomConfHistoryInfo,
    resourceAuditTrailLoginInfo,
} from '../api/resources';

import { getCancelToken } from '../api';

import {
    SUCCESS,
    ROUTE_NAMES,
    BACKUP_FILE_TYPES,
    DEF_DATE_FORMAT,
    MAX_AUDIT_TRAIL_EVENTS,
    MAX_ROOM_HISTORY_EVENTS,
} from '../constants';
import { getFileExtension, ifIsNotCanceled, getFullDateFormatNoSeconds } from '../components/common/helpers';

export default {
    name: 'Maintenance',

    components: {
        DateTimePickerForm,
        DragArea,
        MariTimeClock,
        Modal,
    },

    mixins: [mappingState],

    constants: {
        MAX_AUDIT_TRAIL_EVENTS,
        MAX_ROOM_HISTORY_EVENTS,
    },

    data() {
        return {
            version: null,
            timezone: null,
            file: null,
            exportDate: {
                from: {
                    date: null,
                },
                to: {
                    date: null,
                },
                invalid: false,
            },
            dbStatus: {
                start_date: null,
                activity: null,
                db_size: null,
                entries: null,
            },
            dbRoomHistoryStatus: {
                start_date: null,
                db_size: null,
                entries: null,
            },
            dbAuditTrailStatus: {
                start_date: null,
                db_size: null,
                entries: null,
            },
            cancelToken: getCancelToken(),
            isLoaded: false,
            showModal: false,
        };
    },

    computed: {
        ...mapState({
            authToken: state => state.user.user.token,
        }),

        startDate() {
            return this.dbStatus.start_date
                ? format(parseISO(this.dbStatus.start_date), getFullDateFormatNoSeconds())
                : null;
        },

        startDateRoomHistory() {
            return this.dbRoomHistoryStatus.start_date
                ? format(parseISO(this.dbRoomHistoryStatus.start_date), getFullDateFormatNoSeconds())
                : null;
        },

        startDateAuditTrailLogin() {
            return this.dbAuditTrailStatus.start_date
                ? format(parseISO(this.dbAuditTrailStatus.start_date), getFullDateFormatNoSeconds())
                : null;
        },

        countLogs() {
            return this.dbStatus.entries !== null ? Number(this.dbStatus.entries) : '';
        },

        formattedCountRoomHistory() {
            return this.formatCountLogs(this.dbRoomHistoryStatus.entries);
        },

        formattedCountAuditTrailLogin() {
            return this.formatCountLogs(this.dbAuditTrailStatus.entries);
        },

        formattedCountLogs() {
            // adds space after each three digits
            return this.countLogs !== ''
                ? this.countLogs.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
                : null;
        },

        dbSize() {
            return this.dbStatus.db_size !== null
                ? (Number(this.dbStatus.db_size) / (1024 * 1024)).toFixed(2)
                : '?';
        },

        dbRoomHistorySize() {
            return this.dbRoomHistoryStatus.db_size !== null
                ? (Number(this.dbRoomHistoryStatus.db_size) / (1024 * 1024)).toFixed(2)
                : '?';
        },

        dbAuditTrailLoginSize() {
            return this.dbAuditTrailStatus.db_size !== null
                ? (Number(this.dbAuditTrailStatus.db_size) / (1024 * 1024)).toFixed(2)
                : '?';
        },

        activityHours() {
            return this.dbStatus.activity !== null ? Number(this.dbStatus.activity).toFixed(2) : '?';
        },

        logPercent() {
            return this.countLogs !== '' ? (this.countLogs / 500000) * 100 : 0;
        },

        logRoomHistoryPercent() {
            return this.dbRoomHistoryStatus.entries !== null
                ? (this.dbRoomHistoryStatus.entries / MAX_ROOM_HISTORY_EVENTS) * 100
                : 0;
        },

        logAuditTrailLoginPercent() {
            return this.dbAuditTrailStatus.entries !== null
                ? (this.dbAuditTrailStatus.entries / MAX_AUDIT_TRAIL_EVENTS) * 100
                : 0;
        },

        fromDateTime() {
            return this.exportDate.from.date ? format(this.exportDate.from.date, DEF_DATE_FORMAT) : null;
        },

        toDateTime() {
            return this.exportDate.to.date ? format(this.exportDate.to.date, DEF_DATE_FORMAT) : null;
        },
    },

    created() {
        this.loadStatus(true);
    },

    beforeDestroy() {
        this.cancelToken.cancel();
    },

    methods: {
        loadStatus(withSpinner = false) {
            if (withSpinner) {
                this.showLoading(true);
            }
            let promises = [
                resourceStatus({ cancelToken: this.cancelToken.token }).then(response => {
                    this.dbStatus = response.data;
                }),
                resourceMariInfo({ cancelToken: this.cancelToken.token }).then(res => {
                    this.version = res && res.data && res.data.version;
                    this.timezone = res && res.data && res.data.timezone;
                }),
                resourceRoomConfHistoryInfo({ cancelToken: this.cancelToken.token }).then(res => {
                    this.dbRoomHistoryStatus = res.data;
                }),
                resourceAuditTrailLoginInfo({ cancelToken: this.cancelToken.token }).then(res => {
                    this.dbAuditTrailStatus = res.data;
                }),
            ];
            return Promise.all(promises)
                .then(() => {
                    this.showLoading(false);
                    this.isLoaded = true;
                })
                .catch(
                    ifIsNotCanceled({
                        callback: response => {
                            this.dbStatus = {
                                start_date: null,
                                activity: null,
                                db_size: null,
                                entries: null,
                            };
                            this.version = null;
                            this.timezone = null;
                            this.isLoaded = true;
                            return response;
                        },
                    })
                );
        },

        clearLogs() {
            this.showLoading(true);
            return resourceClearLogs({ method: 'post' })
                .then(this.loadStatus)
                .then(() => {
                    if (this.$router.currentRoute.name === ROUTE_NAMES.MAINTENANCE) {
                        this.showLoading(false);
                        this.popup({
                            type: SUCCESS,
                            header: this.$t('common.success'),
                            text: this.$t('maintenance.successClear'),
                        });
                    }
                })
                .catch(ifIsNotCanceled());
        },

        confirmClearLogs() {
            return this.confirmation({
                confirmTitle: this.$t('maintenance.confirm.title'),
                confirmBody: this.$t('maintenance.confirm.body'),
            }).then(r => {
                if (r) {
                    this.clearLogs();
                }
            });
        },

        confirmClearRoomHistory() {
            return this.confirmation({
                confirmTitle: this.$t('maintenance.confirm.title'),
                confirmBody: this.$t('maintenance.confirmRoomHistory.body'),
            }).then(r => {
                if (r) {
                    this.clearRoomHistory();
                }
            });
        },

        clearRoomHistory() {
            this.showLoading(true);
            return resourceClearRoomConfHistory({ method: 'post' })
                .then(this.loadStatus)
                .then(() => {
                    if (this.$router.currentRoute.name === ROUTE_NAMES.MAINTENANCE) {
                        this.showLoading(false);
                        this.popup({
                            type: SUCCESS,
                            header: this.$t('common.success'),
                            text: this.$t('maintenance.successClear'),
                        });
                    }
                })
                .catch(ifIsNotCanceled());
        },

        confirmClearAuditTrailLogin() {
            return this.confirmation({
                confirmTitle: this.$t('maintenance.confirm.title'),
                confirmBody: this.$t('maintenance.confirmAuditTrailLogin.body'),
            }).then(r => {
                if (r) {
                    this.clearAuditTrailLogin();
                }
            });
        },

        clearAuditTrailLogin() {
            this.showLoading(true);
            return resourceClearAuditTrailLogin({ method: 'post' })
                .then(this.loadStatus)
                .then(() => {
                    if (this.$router.currentRoute.name === ROUTE_NAMES.MAINTENANCE) {
                        this.showLoading(false);
                        this.popup({
                            type: SUCCESS,
                            header: this.$t('common.success'),
                            text: this.$t('maintenance.successClear'),
                        });
                    }
                })
                .catch(ifIsNotCanceled());
        },

        restore() {
            if (this.file) {
                this.showLoading(true);
                const formData = new FormData();
                formData.append('file', this.file, this.file.name);
                return resourceRestore({ method: 'post', data: formData })
                    .then(() => this.loadStatus())
                    .then(() => {
                        if (this.$router.currentRoute.name === ROUTE_NAMES.MAINTENANCE) {
                            this.filesChange(null);
                            this.showLoading(false);
                            this.popup({
                                type: SUCCESS,
                                header: this.$t('common.success'),
                                text: this.$t('maintenance.successRestore'),
                            });
                        }
                    })
                    .catch(ifIsNotCanceled());
            }
        },

        filesChange(file) {
            this.file = null;
            if (file) {
                if (BACKUP_FILE_TYPES.includes(getFileExtension(file))) {
                    this.file = file;
                } else {
                    this.showModal = true;
                }
            } else {
                this.$refs.restore.value = '';
            }
        },

        runLogParser() {
            this.showLoading(true);
            return resourceRunLogParser({ method: 'post' })
                .then(this.loadStatus)
                .then(() => {
                    if (this.$router.currentRoute.name === ROUTE_NAMES.MAINTENANCE) {
                        this.showLoading(false);
                        this.popup({
                            type: SUCCESS,
                            header: this.$t('common.success'),
                            text: this.$t('maintenance.successRunLogParser'),
                        });
                    }
                })
                .catch(ifIsNotCanceled());
        },

        addDroppedFiles(files) {
            if (files.length === 1) {
                this.filesChange(files[0]);
            }
        },

        enterHandler() {
            this.showModal = false;
        },

        formatCountLogs(count) {
            // adds space after each three digits
            return count !== null ? count.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ') : '';
        },
    },
};
</script>
